import { useState, useCallback, useRef, useEffect, useMemo } from "react";

import { SearchTrucksFilterBar } from "@/components/search-trucks/SearchTrucksFilterBar";
import { MobileSearchSummary } from "@/components/search/MobileSearchSummary";
import { SearchTrucksResultsTable } from "@/components/search-trucks/SearchTrucksResultsTable";
import { usePageMeta } from "@/hooks/usePageMeta";
import { supabase } from "@/integrations/supabase/client";
import {
  ActiveSearchTabs,
  createBlankTab as createBlankLoadTab,
} from "@/components/search/ActiveSearchTabs";
import type { TruckSortOption } from "@/components/search-trucks/TrucksResultsToolbar";
import { displayCompanyName } from "@/lib/displayCompanyName";

/* ─── Truck-specific tab types ─────────────────────────────────────── */

export interface TruckSearchTabFilters {
  origin: string;
  destination: string;
  equipmentTypes: string[];
  sortBy: TruckSortOption;
}

export const DEFAULT_TRUCK_FILTERS: TruckSearchTabFilters = {
  origin: "",
  destination: "",
  equipmentTypes: [],
  sortBy: "age_newest",
};

export interface TruckSearchTab {
  id: string;
  label: string;
  filters: TruckSearchTabFilters;
  hasSearched: boolean;
}

export function createBlankTruckTab(): TruckSearchTab {
  return {
    id: crypto.randomUUID(),
    label: "New Search",
    filters: { ...DEFAULT_TRUCK_FILTERS, equipmentTypes: [] },
    hasSearched: false,
  };
}

/* ─── Truck row type ───────────────────────────────────────────────── */

export interface TruckRow {
  id: string;
  created_at: string;
  available_from: string;
  available_to: string;
  origin_location: string;
  destination_location: string | null;
  equipment_type: string;
  length_ft: number | null;
  weight_capacity: number | null;
  rate_per_mile: number | null;
  contact_info: string | null;
  company_name: string | null;
  comments: string | null;
  load_type: string | null;
  reference_id: string | null;
  status: string | null;
  company_id: string | null;
  documents_verified_at: string | null;
}

/* ─── localStorage helpers ─────────────────────────────────────────── */
const STORAGE_KEY = "lr-truck-search-tabs";

function loadTabsFromStorage(): { tabs: TruckSearchTab[]; activeTabId: string } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed.tabs) && parsed.tabs.length > 0) {
        return { tabs: parsed.tabs, activeTabId: parsed.activeTabId || parsed.tabs[0].id };
      }
    }
  } catch {
    // ignore
  }
  return { tabs: [], activeTabId: "" };
}

function saveTabsToStorage(tabs: TruckSearchTab[], activeTabId: string) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ tabs, activeTabId }));
  } catch {
    // ignore
  }
}

/* ─── Build tab label from filters ─────────────────────────────────── */
function buildLabel(filters: TruckSearchTabFilters): string {
  const o = filters.origin?.trim();
  const d = filters.destination?.trim();
  if (o && d) return `${o} → ${d}`;
  if (o) return `${o} → Anywhere`;
  if (d) return `Anywhere → ${d}`;
  return "All Trucks";
}

/* ═══════════════════════════════════════════════════════════════════ */
export default function SearchTrucksPageV2() {
  usePageMeta({ title: "Search Trucks | LaneRunner", description: "Find available carrier capacity" });

  // ── Tab state (persisted to localStorage) ──
  const [tabs, setTabs] = useState<TruckSearchTab[]>(() => loadTabsFromStorage().tabs);
  const [activeTabId, setActiveTabId] = useState<string>(() => loadTabsFromStorage().activeTabId);

  // ── Mobile-only: per-tab collapsed state for the search form ──
  const [formCollapsedMap, setFormCollapsedMap] = useState<Record<string, boolean>>({});

  // ── Pending filters for when no tabs exist ──
  const pendingFiltersRef = useRef<TruckSearchTabFilters>({ ...DEFAULT_TRUCK_FILTERS, equipmentTypes: [] });

  // ── In-memory result cache (not persisted) ──
  const resultsCache = useRef<Map<string, TruckRow[]>>(new Map());

  // ── Searching state ──
  const [isSearching, setIsSearching] = useState(false);

  // Persist tabs to localStorage on change
  useEffect(() => {
    saveTabsToStorage(tabs, activeTabId);
  }, [tabs, activeTabId]);

  // ── Derived: current tab ──
  const activeTab = tabs.find((t) => t.id === activeTabId);
  const cachedResults = activeTab ? resultsCache.current.get(activeTab.id) : undefined;

  // ── Current filters for the filter bar ──
  const currentFilters = activeTab?.filters ?? pendingFiltersRef.current;
  const filterBarKey = activeTabId || "__no-tab__";

  // ── Auto-search on tab switch when cache is missing ──
  useEffect(() => {
    if (!activeTab || !activeTab.hasSearched) return;
    if (isSearching) return;
    if (resultsCache.current.has(activeTab.id)) return;

    // Cache is missing for a previously-searched tab → auto-run
    handleSearch({ origin: activeTab.filters.origin, destination: activeTab.filters.destination, equipmentTypes: activeTab.filters.equipmentTypes });
  }, [activeTabId]); // intentionally minimal deps

  // ── Update a specific tab ──
  const updateTab = useCallback((tabId: string, patch: Partial<TruckSearchTab>) => {
    setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, ...patch } : t)));
  }, []);

  // ── Add tab with overflow (evict oldest if >= 10) ──
  const addTabWithOverflow = useCallback(
    (newTab: TruckSearchTab) => {
      setTabs((prev) => {
        if (prev.length >= 10) {
          const oldest = prev[0];
          resultsCache.current.delete(oldest.id);
          return [...prev.slice(1), newTab];
        }
        return [...prev, newTab];
      });
      setActiveTabId(newTab.id);
    },
    [],
  );

  // ── Filter change from SearchTrucksFilterBar ──
  const handleFilterChange = useCallback(
    (filters: TruckSearchTabFilters) => {
      if (activeTab) {
        updateTab(activeTab.id, { filters });
      } else {
        pendingFiltersRef.current = filters;
      }
    },
    [activeTab, updateTab],
  );

  // ── Search ──
  const handleSearch = useCallback(
    async (params: { origin: string; destination: string; equipmentTypes: string[] }) => {
      setIsSearching(true);

      const newFilters: TruckSearchTabFilters = {
        origin: params.origin,
        destination: params.destination,
        equipmentTypes: params.equipmentTypes,
        sortBy: activeTab?.filters.sortBy ?? "age_newest",
      };
      const label = buildLabel(newFilters);

      // If no tabs exist, auto-create the first tab
      let targetTabId: string;
      if (!activeTab) {
        const newTab: TruckSearchTab = {
          id: crypto.randomUUID(),
          label,
          filters: newFilters,
          hasSearched: true,
        };
        targetTabId = newTab.id;
        addTabWithOverflow(newTab);
      } else {
        targetTabId = activeTab.id;
        updateTab(activeTab.id, { filters: newFilters, label, hasSearched: true });
      }

      try {
        const today = new Date().toISOString().split("T")[0];
        let query = supabase
          .from("truck_postings")
          .select("*, companies(name, documents_verified_at)")
          .in("status", ["active", "available"])
          .gte("available_to", today)
          .order("created_at", { ascending: false })
          .limit(100);

        if (params.equipmentTypes.length > 0) {
          query = query.in("equipment_type", params.equipmentTypes as any);
        }

        if (params.origin) {
          const originCity = params.origin.split(",")[0].trim();
          if (originCity) query = query.ilike("origin_location", `%${originCity}%`);
        }

        if (params.destination) {
          const destCity = params.destination.split(",")[0].trim();
          if (destCity) query = query.ilike("destination_location", `%${destCity}%`);
        }

        const { data, error } = await query;
        if (error) throw error;

        const trucks: TruckRow[] = (data || []).map((t: any) => ({
          id: t.id,
          created_at: t.created_at,
          available_from: t.available_from,
          available_to: t.available_to,
          origin_location: t.origin_location,
          destination_location: t.destination_location,
          equipment_type: t.equipment_type,
          length_ft: t.length_ft,
          weight_capacity: t.weight_capacity_lbs,
          rate_per_mile: t.rate_per_mile,
          contact_info: t.contact_info,
          company_name: displayCompanyName(t.companies?.name) || null,
          documents_verified_at: t.companies?.documents_verified_at ?? null,
          comments: t.comments || null,
          load_type: t.load_type || null,
          reference_id: t.reference_id || null,
          status: t.status || null,
          company_id: t.company_id || null,
        }));

        resultsCache.current.set(targetTabId, trucks);
      } catch (err) {
        console.error("Search trucks error:", err);
        resultsCache.current.set(targetTabId, []);
      } finally {
        setIsSearching(false);
      }
    },
    [activeTab, updateTab, addTabWithOverflow],
  );

  // ── Tab actions ──
  const handleAddTab = useCallback(() => {
    const newTab = createBlankTruckTab();
    addTabWithOverflow(newTab);
  }, [addTabWithOverflow]);

  const handleCloseTab = useCallback(
    (tabId: string) => {
      setFormCollapsedMap(prev => { if (!(tabId in prev)) return prev; const n = { ...prev }; delete n[tabId]; return n; });
      if (tabs.length <= 1) {
        resultsCache.current.delete(tabId);
        pendingFiltersRef.current = { ...DEFAULT_TRUCK_FILTERS, equipmentTypes: [] };
        setTabs([]);
        setActiveTabId("");
        return;
      }
      const idx = tabs.findIndex((t) => t.id === tabId);
      const newTabs = tabs.filter((t) => t.id !== tabId);
      resultsCache.current.delete(tabId);

      if (activeTabId === tabId) {
        const newActive = newTabs[Math.max(0, idx - 1)];
        setActiveTabId(newActive.id);
      }
      setTabs(newTabs);
    },
    [tabs, activeTabId],
  );

  const handleSelectTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  // ── Derive display state ──
  const displayTrucks = cachedResults ?? [];
  const hasSearched = activeTab?.hasSearched ?? false;
  const currentSortBy = activeTab?.filters.sortBy ?? "age_newest";

  const handleSortChange = useCallback((sort: TruckSortOption) => {
    if (!activeTab) return;
    updateTab(activeTab.id, {
      filters: { ...activeTab.filters, sortBy: sort },
    });
  }, [activeTab, updateTab]);

  // Adapt tabs for ActiveSearchTabs (which expects SearchTab shape)
  const adaptedTabs = useMemo(() =>
    tabs.map((t) => ({
      id: t.id,
      label: t.label,
      filters: t.filters as any,
      hasSearched: t.hasSearched,
    })),
    [tabs],
  );

  return (
    <div className="flex flex-col w-full h-full">
      {/* ═══ Active Search Tabs ═══ */}
      <ActiveSearchTabs
        tabs={adaptedTabs}
        activeTabId={activeTabId}
        onSelectTab={handleSelectTab}
        onAddTab={handleAddTab}
        onCloseTab={handleCloseTab}
      />

      {/* ═══ Mobile collapsed summary + Filter Bar ═══ */}
      {(() => {
        const isFormCollapsed = activeTabId
          ? (formCollapsedMap[activeTabId] ?? !!activeTab?.hasSearched)
          : false;
        return (
          <>
            {isFormCollapsed && (
              <MobileSearchSummary
                origin={currentFilters.origin}
                destination={currentFilters.destination}
                equipmentTypes={currentFilters.equipmentTypes}
                onEdit={() => {
                  if (activeTabId) {
                    setFormCollapsedMap(prev => ({ ...prev, [activeTabId]: false }));
                  }
                }}
              />
            )}
            <div className={isFormCollapsed ? "hidden md:block" : ""}>
              <SearchTrucksFilterBar
                key={filterBarKey}
                onSearch={(params) => {
                  if (activeTabId) {
                    setFormCollapsedMap(prev => ({ ...prev, [activeTabId]: true }));
                  }
                  handleSearch(params);
                }}
                isSearching={isSearching}
                filterValues={currentFilters}
                onFilterChange={handleFilterChange}
              />
            </div>
          </>
        );
      })()}
      {/* ═══ Results (contained scroll area) ═══ */}
      {hasSearched && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <SearchTrucksResultsTable
            trucks={displayTrucks}
            isLoading={isSearching}
            sortBy={currentSortBy}
            onSortChange={handleSortChange}
          />
        </div>
      )}
      {!hasSearched && (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2">
          <span className="text-sm">
            Enter search criteria and click Search Trucks to find available capacity.
          </span>
        </div>
      )}
    </div>
  );
}
